import React, { useState, useEffect } from 'react';
import { X, RotateCw, Crop, Info } from 'lucide-react';
import ImageCropper from './image-cropper';
import { useTranslation } from 'react-i18next';

interface ImageResizePopupProps {
    onEnhancedImage?: (config: ResizeConfig) => void;
    image?: any,
}

interface ResizeConfig {
    width?: number;
    height?: number;
    maintainRatio?: boolean;
    saveForLater?: boolean;
    portraitToLandscape?: boolean;
    enableCropping?: boolean;
    croppedImageUrl?: string;
}

const ImageResize: React.FC<ImageResizePopupProps> = ({ onEnhancedImage, image }) => {

    const [selectedRatio, setSelectedRatio] = useState<string>('');
    const [width, setWidth] = useState<number>(800);
    const [height, setHeight] = useState<number>(800);
    const [maintainRatio, setMaintainRatio] = useState<boolean>(false);
    const [enableCropping, setEnableCropping] = useState<boolean>(false);
    const [showCropper, setShowCropper] = useState<boolean>(false);
    const [croppedImageUrl, setCroppedImageUrl] = useState<string>('');
    const [currentImageSize, setCurrentImageSize] = useState<{ width: number; height: number } | null>(null);
    const { t } = useTranslation();

    const portalSizes = [
        { label: "Property Finder", value: "Property Finder", width: 800, height: 600 },
        { label: "Bayut", value: "Bayut", width: 800, height: 600 },
        { label: "Dubizzle", value: "Dubizzle", width: 800, height: 600 }
    ];

    useEffect(() => {
        // Reset and get current image dimensions
        setCurrentImageSize(null);
        if (image && image[0]?.src) {
            const img = new Image();
            img.onload = () => {
                setCurrentImageSize({ width: img.width, height: img.height });
            };
            img.onerror = () => {
                console.error("Failed to load image for size detection");
            };
            img.src = image[0].src;
        }
    }, [image?.[0]?.src]);

    const handlePortalSelect = (portal: typeof portalSizes[0]) => {
        setSelectedRatio(portal.value);
        setWidth(portal.width);
        setHeight(portal.height);
    };

    const handleWidthChange = (newWidth: number) => {
        setWidth(newWidth);
        if (maintainRatio && currentImageSize) {
            const aspectRatio = currentImageSize.width / currentImageSize.height;
            setHeight(Math.round(newWidth / aspectRatio));
        }
    };

    const handleHeightChange = (newHeight: number) => {
        setHeight(newHeight);
        if (maintainRatio && currentImageSize) {
            const aspectRatio = currentImageSize.width / currentImageSize.height;
            setWidth(Math.round(newHeight * aspectRatio));
        }
    };

    const handleResize = () => {
        const enhanceObj = {
            "id": '',
            "images": image,
            "width": width,
            "height": height,
            "maintain_img_ratio": maintainRatio,
            enableCropping,
            ...(enableCropping ? { croppedImageUrl } : { croppedImageUrl: '' })
        }
        onEnhancedImage?.(enhanceObj);
    };

    const handleCroppingToggle = (checked: boolean) => {
        setEnableCropping(checked);
        if (checked) {
            setShowCropper(true);
        }
    };

    const handleCropComplete = (croppedUrl: string) => {
        setCroppedImageUrl(croppedUrl);
        setShowCropper(false);
    };

    const CloseCropper = () => {
        setShowCropper(false);
        setEnableCropping(false);
    }

    return (
        <div className="card border-20">
            {!showCropper ? <div className="padd-20">

                {/* Portal Sizes */}
                <h3 className="title-20 title-18-mb bold-700 mb-3">{t("resizeImage.portalSizes")}</h3>
                <div className="grid-size">
                    {portalSizes.map((portal) => (
                        <label key={portal.value} className="option">
                            <input
                                type="radio"
                                name="portal"
                                value={portal.value}
                                checked={selectedRatio === portal.value}
                                onChange={(e) => handlePortalSelect(portal)}
                            />
                            {portal.label}
                            <span className="checkmark"></span>
                        </label>
                    ))}
                </div>

                {/* Current and New Size Display */}
                {currentImageSize && (
                    <div className="mt-3 mb-3 p-3" style={{ background: '#F8FAFC', borderRadius: '8px' }}>
                        <div className="d-flex justify-content-between align-items-center mb-2">
                            <div>
                                <span className="para-14 color-475569">{t("resizeImage.currentSize")}: </span>
                                <span className="para-14 bold-600">{currentImageSize.width} × {currentImageSize.height}px</span>
                            </div>
                            <div>
                                <span className="para-14 color-475569">{t("resizeImage.newSize")}: </span>
                                <span className="para-14 bold-600">{width} × {height}px</span>
                            </div>
                        </div>
                    </div>
                )}

                {/* OR Divider */}
                <div className="separator">{t("resizeImage.or")}</div>

                {/* Custom Dimensions */}
                <div className="d-flex gap-8">
                    <div className="col-1  mb-3">
                        <label className="form-label-2 bold-600 mb-2" htmlFor="selectTypeOpt">{t("resizeImage.width")}</label>
                        <input type="number" value={width}
                            onChange={(e) => handleWidthChange(Number(e.target.value))} className=" form-control input-size " placeholder="800" />
                    </div>
                    <div className="col-1  mb-3">
                        <label className="form-label-2 bold-600 mb-2" htmlFor="selectTypeOpt">{t("resizeImage.height")}</label>
                        <input type="number" placeholder="800" value={height}
                            onChange={(e) => handleHeightChange(Number(e.target.value))} className="form-control input-size" />
                    </div>
                </div>

                {/* Options */}
                <div className="d-flex gap-8">
                    <div className="col-1  mb-3">
                        <div className="form-check">
                            <input className="form-check-input" type="checkbox" checked={maintainRatio}
                                onChange={(e) => setMaintainRatio(e.target.checked)} id="defaultCheck3" />
                            <label className="form-check-label-1 para-14-mb" htmlFor="defaultCheck3">{t("resizeImage.maintainImageRatio")}</label>
                        </div>
                    </div>
                </div>

                {/* New Features */}
                <h3 className="title-20 title-18-mb bold-700 mb-4 mt-2">Advanced Features</h3>

                <div className="grid-size">
                    <label className={`option ${Array.isArray(image) && image.length > 1 ? 'disabled' : ''}`}>
                        <input
                            type="checkbox"
                            checked={enableCropping}
                            onChange={(e) => handleCroppingToggle(e.target.checked)}
                            disabled={Array.isArray(image) && image.length > 1}
                        />
                        Enable cropping
                        <span className="checkmark"></span>
                    </label>
                </div>

                {/* Resize Button */}
                <button type="button" className={`mt-4 button-dash back-746AFF color-FFFFFF  border-746AFF padd-15 width-100-100 `}
                    onClick={handleResize}>{t("resizeImage.resize")}</button>
            </div> :
                <div className="padd-20">
                    <ImageCropper
                        isOpen={showCropper}
                        onClose={CloseCropper}
                        onCropComplete={handleCropComplete}
                        imageUrl={image[0]?.src || ''}
                        aspectRatio={selectedRatio === '1:1' ? 1 : selectedRatio === '16:9' ? 16 / 9 : selectedRatio === '9:16' ? 9 / 16 : undefined}
                    />
                </div>}
        </div >
    );
};

export default ImageResize;